import React, { useState, useEffect } from 'react';
import {
  FileText, ChevronRight, ChevronDown, Folder, Search,
  Eye, ArrowRight, ArrowLeft, CheckCircle, Package,
  X, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import ResponsiveModal from './ResponsiveModal';
import { SaveButton, CancelButton } from './CommonButtons';
import { FormLoader } from './CommonLoader';
import dataroomTemplateService from '../services/dataroomTemplateService';
import Switch from './Switch';

const ImportTemplateModal = ({ isOpen, onClose, onImport, dataroomId, currentFolderName }) => {
  // Note: dataroomId is the dataroom UUID
  const [currentStep, setCurrentStep] = useState(1); // 1: List, 2: Preview
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('true'); // Only show active templates by default
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [folderStructure, setFolderStructure] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  // Fetch templates when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      setCurrentStep(1);
      setSelectedTemplate(null);
      setSelectedTemplateId(null);
      setFolderStructure([]);
      setExpandedFolders(new Set());
    }
  }, [isOpen]);

  // Fetch available templates using List Available Templates API
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // Use the List Available Templates API endpoint for dataroom owners
      const response = await dataroomTemplateService.getAvailableTemplates({
        page: 1,
        per_page: 100, // Get all templates
        search: searchTerm
      });

      console.log('Templates response:', response);

      // Handle different response structures
      let templatesArray = [];
      if (Array.isArray(response)) {
        templatesArray = response;
      } else if (response.templates) {
        templatesArray = response.templates;
      } else if (response.data) {
        if (Array.isArray(response.data)) {
          templatesArray = response.data;
        } else if (response.data.templates) {
          templatesArray = response.data.templates;
        } else if (response.data.data) {
          templatesArray = response.data.data;
        }
      }

      // Ensure each template has proper folder count
      const templatesWithCount = templatesArray.map(template => {
        // Try to get folder count from various possible fields
        let folderCount = 0;

        // Check all possible folder count field names
        if (template.folder_count !== undefined && template.folder_count !== null) {
          folderCount = parseInt(template.folder_count);
        } else if (template.folders_count !== undefined && template.folders_count !== null) {
          folderCount = parseInt(template.folders_count);
        } else if (template.folder_counts !== undefined && template.folder_counts !== null) {
          folderCount = parseInt(template.folder_counts);
        } else if (template.total_folders !== undefined && template.total_folders !== null) {
          folderCount = parseInt(template.total_folders);
        } else if (template.folders && Array.isArray(template.folders)) {
          // Count folders recursively if we have the structure
          const countFolders = (folders) => {
            let count = folders.length;
            folders.forEach(folder => {
              if (folder.children && Array.isArray(folder.children)) {
                count += countFolders(folder.children);
              }
            });
            return count;
          };
          folderCount = countFolders(template.folders);
        }

        console.log(`Template ${template.name} - Folder count: ${folderCount}`);

        return {
          ...template,
          folder_count: folderCount
        };
      });

      setTemplates(templatesWithCount);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to fetch templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle template selection (just store the selection, don't fetch details yet)
  const handleSelectTemplate = (template) => {
    setSelectedTemplateId(template.id);
    setSelectedTemplate(template);
  };

  // Handle Next button - fetch full template details
  const handleNext = async () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template first');
      return;
    }

    setLoading(true);
    try {
      // Fetch full template details using Get Template API for dataroom owners
      const response = await dataroomTemplateService.getTemplate(selectedTemplateId);
      console.log('Full template response:', response);

      // Handle nested response structure
      const fullTemplate = response.template || response.data?.template || response.data || response;
      console.log('Full template data:', fullTemplate);

      // Update selected template with full details
      setSelectedTemplate(fullTemplate);
      setFolderStructure(fullTemplate.folders || fullTemplate.folder_structure || []);

      // Auto-expand first level folders
      const initialExpanded = new Set();
      const folders = fullTemplate.folders || fullTemplate.folder_structure || [];
      if (folders.length > 0) {
        folders.forEach((folder, index) => {
          initialExpanded.add(`${index}`);
        });
      }
      setExpandedFolders(initialExpanded);
      setCurrentStep(2);
    } catch (error) {
      console.error('Failed to fetch template details:', error);
      toast.error('Failed to fetch template details');
    } finally {
      setLoading(false);
    }
  };

  // Handle import
  const handleImport = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    setImporting(true);
    try {
      // Call parent's onImport function with selected template
      await onImport(selectedTemplate);
      onClose();
    } catch (error) {
      console.error('Failed to import template:', error);
    } finally {
      setImporting(false);
    }
  };

  // Count folders recursively
  const countFoldersRecursive = (folders) => {
    if (!folders || !Array.isArray(folders)) return 0;
    let count = folders.length;
    folders.forEach(folder => {
      if (folder.children && Array.isArray(folder.children)) {
        count += countFoldersRecursive(folder.children);
      }
    });
    return count;
  };

  // Toggle folder expansion
  const toggleFolderExpansion = (folderPath) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    setExpandedFolders(newExpanded);
  };

  // Render folder structure for preview
  const renderFolderStructure = (folders, parentPath = [], level = 0) => {
    if (!folders || !Array.isArray(folders) || folders.length === 0) {
      return null;
    }

    return (
      <div className={level === 0 ? '' : 'ml-6'}>
        {folders.map((folder, index) => {
          const currentPath = [...parentPath, index];
          const pathStr = currentPath.join('-');
          const isExpanded = expandedFolders.has(pathStr);
          const hasChildren = folder.children && folder.children.length > 0;

          return (
            <div key={pathStr} className="mb-2">
              <div className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                <button
                  onClick={() => toggleFolderExpansion(pathStr)}
                  className="p-0.5 hover:bg-gray-100 rounded"
                  type="button"
                >
                  {hasChildren ? (
                    isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                  ) : (
                    <div className="w-4" />
                  )}
                </button>
                <Folder size={16} className="text-yellow-500" />
                <span className="flex-1 font-medium">{folder.name}</span>
              </div>
              {isExpanded && hasChildren && (
                <div className="ml-6 mt-2">
                  {renderFolderStructure(folder.children, currentPath, level + 1)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Folder Template"
      size="xlarge"
    >
      <div className="space-y-4">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center ${currentStep === 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                1
              </div>
              <span className="ml-2 font-medium">Select Template</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
            <div className={`flex items-center ${currentStep === 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep === 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                2
              </div>
              <span className="ml-2 font-medium">Preview & Import</span>
            </div>
          </div>
        </div>

        {/* Step 1: Template List */}
        {currentStep === 1 && (
          <div className="space-y-4">
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchTemplates()}
                  placeholder="Search templates..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Templates List */}
            <div className="border rounded-lg overflow-hidden min-h-[400px]">
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
                  <p className="text-gray-500">Loading templates...</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="p-8 text-center">
                  <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 font-medium">No templates available</p>
                  <p className="text-sm text-gray-400 mt-2">Please create templates first in Predefined Templates</p>
                </div>
              ) : (
                <div className="max-h-[500px] overflow-y-auto">
                  {templates.map((template) => {
                    const isSelected = selectedTemplateId === template.id;
                    return (
                      <div
                        key={template.id}
                        className={`border-b last:border-b-0 transition-colors cursor-pointer ${
                          isSelected ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <div className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  name="template-selection"
                                  checked={isSelected}
                                  onChange={() => handleSelectTemplate(template)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                                <FileText className="w-5 h-5 text-gray-400" />
                                <h3 className="font-medium text-gray-900">{template.name}</h3>
                                {template.is_active && (
                                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                    Active
                                  </span>
                                )}
                              </div>
                              {template.description && (
                                <p className="text-sm text-gray-500 mt-1 ml-9">{template.description}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 ml-9">
                                <span className={`text-sm font-medium ${
                                  template.folder_count > 0 ? 'text-blue-600' : 'text-gray-400'
                                }`}>
                                  <Folder className="w-4 h-4 inline mr-1" />
                                  {template.folder_count > 0 ? (
                                    <>{template.folder_count} folders</>
                                  ) : (
                                    'No folders'
                                  )}
                                </span>
                                {template.created_at && (
                                  <span className="text-xs text-gray-400">
                                    Created: {new Date(template.created_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Preview & Import */}
        {currentStep === 2 && selectedTemplate && (
          <div>
            <div className="space-y-4">
              {/* Template Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{selectedTemplate.name}</h3>
                    {selectedTemplate.description && (
                      <p className="text-sm text-gray-600 mt-1">{selectedTemplate.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-sm font-medium text-blue-600">
                        <Folder className="w-4 h-4 inline mr-1" />
                        Total: {countFoldersRecursive(folderStructure)} folders
                      </span>
                      {selectedTemplate.is_active && (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                          Active Template
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Folder Structure Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Folder Structure Preview
                </label>
                <div className="border rounded-lg p-4 max-h-96 overflow-y-auto bg-gray-50">
                  {folderStructure.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No folders in this template</p>
                  ) : (
                    renderFolderStructure(folderStructure)
                  )}
                </div>
              </div>

              {/* Import Note */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-gray-700">
                    <p className="font-medium mb-1">This will import the following:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      <li>All folder structure from this template</li>
                      <li>Folders will be created {currentFolderName ? `in "${currentFolderName}"` : 'in your dataroom'}</li>
                      <li>Existing folders will not be affected</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex justify-between items-center pt-4 border-t">
          {currentStep === 1 ? (
            <div className="flex justify-between w-full">
              <CancelButton onClick={onClose}>
                Cancel
              </CancelButton>
              <button
                onClick={handleNext}
                disabled={!selectedTemplateId || loading}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium transition-colors ${
                  !selectedTemplateId || loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          ) : currentStep === 2 ? (
            <>
              <button
                onClick={() => setCurrentStep(1)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Templates
              </button>
              <div className="flex gap-3">
                <CancelButton onClick={onClose}>
                  Cancel
                </CancelButton>
                <SaveButton onClick={handleImport} disabled={importing}>
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Importing...
                    </>
                  ) : (
                    'Import Template'
                  )}
                </SaveButton>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default ImportTemplateModal;